/* =============================================================================
   A recording, opened over the page.

   Chrome only — the player, the transport and the download all belong to
   `MediaPlayer`. This adds a title, a way out, and a scrim.

   The scrim is NEUTRAL on purpose. It used to be the brand ink at 97%, which is
   a violet, and a violet wash over a video is a colour cast on somebody's face.
   A screen behind a video should disappear.
   ============================================================================= */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { MediaPlayer, type MediaSource } from "./MediaPlayer";
import { Icon } from "../ui/Icon";

export function MediaModal({
  source,
  title,
  meta,
  poster,
  downloadUrl,
  onClose,
  onEnded,
}: {
  source: MediaSource;
  title: string;
  meta?: string;
  poster?: string | null;
  downloadUrl?: string | null;
  onClose: () => void;
  onEnded?: () => void;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      // Fullscreen takes Esc first, or closing would leave the browser
      // fullscreen with nothing in it.
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div className="vu-media-modal" role="dialog" aria-modal="true" aria-label={title}>
      <header className="vu-media-modal__head">
        <span className="vu-media-modal__title">{title}</span>
        {meta && <span className="vu-media-modal__meta">{meta}</span>}
        <button
          type="button"
          className="vu-media-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon name="xmark" size="1.1rem" />
        </button>
      </header>

      <div className="vu-media-modal__stage">
        <MediaPlayer
          source={source}
          poster={poster}
          title={title}
          downloadUrl={downloadUrl}
          autoPlay
          onEnded={onEnded}
        />
      </div>
    </div>,
    document.body,
  );
}
